import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getListSupportedCitiesQueryOptions } from "@/api/orval";
import type { RoutingCity } from "@/api/orval-types";
import { getOSRMRoute, isSelfHostedRouting } from "@/lib/routing";
import { routeElevation } from "@/lib/elevation";

export interface Waypoint {
  coordinates: [number, number];
  label: string;
}

export const useRouting = () => {
  const [routingMode, setRoutingMode] = useState(false);
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [routeGeometry, setRouteGeometry] = useState<any>(null);
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [elevationGain, setElevationGain] = useState<number | null>(null);
  const [history, setHistory] = useState<Waypoint[][]>([[]]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const queryClient = useQueryClient();

  // Sample terrain elevation for whatever route is currently drawn (manual,
  // reversed, or a picked suggestion). Async and best-effort: it clears the
  // number if nothing can be sampled.
  useEffect(() => {
    const coords = routeGeometry?.geometry?.coordinates as
      | [number, number][]
      | undefined;
    if (!coords || coords.length < 2) {
      setElevationGain(null);
      return;
    }
    let cancelled = false;
    routeElevation(coords)
      .then((e) => {
        if (!cancelled) setElevationGain(e.gain);
      })
      .catch(() => {
        if (!cancelled) setElevationGain(null);
      });
    return () => {
      cancelled = true;
    };
  }, [routeGeometry]);

  // Cities we self-host use the backend proxy; everything else keeps using the
  // public routing path. Decided from the first waypoint's city. Resolve the
  // supported list before deciding (ensureQueryData awaits an in-flight load
  // and returns cache otherwise) so a route placed before the list loads is not
  // wrongly sent to the public path.
  const routeWaypoints = useCallback(
    async (coordinates: [number, number][]) => {
      if (coordinates.length === 0) return;
      let supportedCities: RoutingCity[];
      try {
        supportedCities = await queryClient.ensureQueryData(
          getListSupportedCitiesQueryOptions(),
        );
      } catch {
        supportedCities = [];
      }
      const selfHosted = isSelfHostedRouting(coordinates[0], supportedCities);
      await getOSRMRoute(
        coordinates,
        setRouteGeometry,
        setTotalDistance,
        selfHosted,
      );
    },
    [queryClient],
  );

  const addWaypoint = useCallback(
    async (coordinates: [number, number]) => {
      const newWaypoint: Waypoint = {
        coordinates,
        label: `Point ${waypoints.length + 1}`,
      };

      const newWaypoints = [...waypoints, newWaypoint];

      const newHistory = history.slice(0, currentIndex + 1);
      newHistory.push(newWaypoints);
      setHistory(newHistory);
      setCurrentIndex(currentIndex + 1);
      setWaypoints(newWaypoints);

      if (newWaypoints.length >= 2) {
        await routeWaypoints(newWaypoints.map((wp) => wp.coordinates));
      }
    },
    [waypoints, history, currentIndex, routeWaypoints],
  );

  const undo = useCallback(async () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const newWaypoints = history[newIndex];
      setCurrentIndex(newIndex);
      setWaypoints(newWaypoints);

      if (newWaypoints.length >= 2) {
        await routeWaypoints(newWaypoints.map((wp) => wp.coordinates));
      } else {
        setRouteGeometry(null);
        setTotalDistance(0);
      }
    }
  }, [currentIndex, history, routeWaypoints]);

  const redo = useCallback(async () => {
    if (currentIndex < history.length - 1) {
      const newIndex = currentIndex + 1;
      const newWaypoints = history[newIndex];
      setCurrentIndex(newIndex);
      setWaypoints(newWaypoints);

      if (newWaypoints.length >= 2) {
        await routeWaypoints(newWaypoints.map((wp) => wp.coordinates));
      }
    }
  }, [currentIndex, history, routeWaypoints]);

  const clear = useCallback(() => {
    const newHistory = [...history, []];
    setHistory(newHistory);
    setCurrentIndex(newHistory.length - 1);
    setWaypoints([]);
    setRouteGeometry(null);
    setTotalDistance(0);
  }, [history]);

  const toggleRoutingMode = useCallback(() => {
    setRoutingMode((prev) => !prev);
    if (routingMode) {
      clear();
    }
  }, [routingMode, clear]);

  // Load a ready-made route (e.g. a picked suggested route) onto the map. The
  // feature funnels into the same `route` source effect and the Download button,
  // since exportGPX reads routeGeometry.geometry.coordinates. `distance` is the
  // route length in meters, taken from the suggestion's properties.
  const loadRoute = useCallback((feature: any, distance: number) => {
    setRouteGeometry(feature);
    setTotalDistance(distance);
  }, []);

  // Reverse the current route in place. Distance is unchanged and the reversed
  // coordinates flow through to the map and GPX export.
  const reverseRoute = useCallback(() => {
    setRouteGeometry((prev: any) => {
      if (!prev?.geometry?.coordinates) return prev;
      return {
        ...prev,
        geometry: {
          ...prev.geometry,
          coordinates: [...prev.geometry.coordinates].reverse(),
        },
      };
    });
  }, []);

  const exportGPX = useCallback(() => {
    if (!routeGeometry) return;

    const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Route Builder">
  <trk>
    <name>Generated Route</name>
    <trkseg>
      ${routeGeometry.geometry.coordinates
        .map(
          (coord: [number, number]) =>
            `      <trkpt lat="${coord[1]}" lon="${coord[0]}"></trkpt>`,
        )
        .join("\n")}
    </trkseg>
  </trk>
</gpx>`;

    const blob = new Blob([gpx], { type: "application/gpx+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "route.gpx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [routeGeometry]);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  return {
    routingMode,
    waypoints,
    routeGeometry,
    totalDistance,
    elevationGain,
    canUndo,
    canRedo,
    addWaypoint,
    undo,
    redo,
    clear,
    toggleRoutingMode,
    exportGPX,
    loadRoute,
    reverseRoute,
  };
};
